import { forwardRef, type HTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn.js";

/**
 * Glass — the base translucent surface for Kinetic Glass.
 *
 * Levels follow `docs/DESIGN.md` ("Elevation & Depth"):
 *   - `panel`     Level 1, 20px blur, subtle border
 *   - `floating`  Level 2, 40px blur, rim-light border (top + left brighter)
 *
 * `focus` adds the Pitch Lime outer glow used for selected/active elements.
 */
const glassVariants = cva(
  // base: dark, rounded, subtle inner contour. Border-color is set per variant.
  "relative isolate rounded-lg border text-foreground backdrop-saturate-150",
  {
    variants: {
      level: {
        panel: "bg-white/6 backdrop-blur-panel border-white/10",
        floating:
          "bg-white/12 backdrop-blur-floating border-white/16 [border-top-color:rgba(255,255,255,0.28)] [border-left-color:rgba(255,255,255,0.22)]",
      },
      focus: {
        true: "shadow-glow border-pitch-lime/50",
        false: "",
      },
      padding: {
        none: "p-0",
        sm: "p-3",
        md: "p-4",
        lg: "p-6",
      },
    },
    defaultVariants: {
      level: "panel",
      focus: false,
      padding: "md",
    },
  },
);

export interface GlassProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof glassVariants> {}

export const Glass = forwardRef<HTMLDivElement, GlassProps>(function Glass(
  { className, level, focus, padding, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      data-glass-level={level ?? "panel"}
      className={cn(glassVariants({ level, focus, padding }), className)}
      {...props}
    />
  );
});
